import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

import {
  askAboutDuplicateExtension,
  DuplicateExtensionDismissed,
  isDuplicateExtensionDismissed,
  readDuplicateExtension,
} from "~/modules/Products/hooks/duplicateExtension";
import { readDuplicatePurchase } from "~/modules/Products/hooks/duplicatePurchase";
import { productApi } from "~/store/initializeStore";

/** Komunikat z odpowiedzi serwisu, o ile go przysłał. */
const getServerMessage = (error: unknown): string | null => {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;

  return typeof message === "string" && message.trim() ? message : null;
};

/**
 * Potwierdzenie płatności manualnej własnego leada przez sprzedawcę
 * (TRE-2006). Dokument fiskalny NIE jest wystawiany — serwer wymusza `skip`,
 * a paragon/fakturę wystawia księgowość, gdy przelew trafi na konto.
 */
export const useConfirmSellerManualPayment = () => {
  const queryClient = useQueryClient();
  const key = "confirmSellerManualPayment";

  return useMutation({
    mutationFn: async (payload: { productId: string; orderId: string }) => {
      message.loading({ content: "Trwa potwierdzanie płatności...", key });

      try {
        const response = await productApi.post("/manual-payment/confirm-as-seller", payload);
        return response.data;
      } catch (error) {
        // Dwa zamówienia z tego samego landingu potrafią wpaść minutę po sobie —
        // sprzedawca dostaje pytanie, zanim doszyjemy drugi taki sam pakiet (TRE-2036).
        const duplicate = readDuplicateExtension(error);

        if (!duplicate) {
          throw error;
        }

        message.destroy(key);

        if (!(await askAboutDuplicateExtension(duplicate))) {
          throw new DuplicateExtensionDismissed();
        }

        message.loading({ content: "Trwa potwierdzanie płatności...", key });
        const response = await productApi.post("/manual-payment/confirm-as-seller", {
          ...payload,
          allowDuplicateExtension: true,
        });

        return response.data;
      }
    },
    onSuccess: () => {
      message.destroy(key);
      void message.success("Płatność potwierdzona — dokument wystawi księgowość po zaksięgowaniu przelewu");
      void queryClient.invalidateQueries({ queryKey: ["salesCrmPendingManualOrders"] });
      void queryClient.invalidateQueries({ queryKey: ["salesCrmMeetings"] });
    },
    onError: (error) => {
      message.destroy(key);

      // Sprzedawca sam wybrał „Nie dodawaj" — nic nie poszło źle.
      if (isDuplicateExtensionDismissed(error)) {
        return;
      }

      // Klientka ma już opłacone to samo zamówienie sprzed chwili (TRE-2055).
      // Sprzedawca nie ma flagi obejścia — dostaje wyjaśnienie, a lista „do
      // potwierdzenia" po odświeżeniu chowa ten przycisk.
      if (readDuplicatePurchase(error)) {
        void queryClient.invalidateQueries({ queryKey: ["salesCrmPendingManualOrders"] });
      }

      // Serwis mówi DLACZEGO się nie da (cudzy lead, zamówienie już opłacone) —
      // generyczny komunikat zamieniałby tę informację w „coś poszło nie tak".
      void message.error(getServerMessage(error) ?? "Nie udało się potwierdzić płatności");
    },
  });
};
